import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducerState } from "../store/IRootReducer";
import { setUserTimezone } from "../store/slices/userPreferencesSlice";
import { resolveDisplayTz } from "../common/utilities/displayTime";
import { systemDisplayTimezone } from "../common/constants/displayTimezones";
import { getAuthorizationHeader } from "../utils/authService";
import { logger } from "../services/logger.service";

/**
 * The IANA zone to render times in, for the current viewer.
 *
 *   const zone = useDisplayTz();
 *   formatInstant(application.appliedAt, zone);
 *
 * Every user-facing timestamp should resolve its zone through this hook rather
 * than letting the rendering machine decide. SkyHire runs Indian and US tenants
 * off one deployment, so "whatever zone this browser is in" means a recruiter in
 * Pune and a recruiter in Portland read the same interview slot as two different
 * times, and neither can tell which one the other saw.
 */
export function useDisplayTz(): string {
  const userTz = useSelector((s: IRootReducerState) => s.userPreferencesReducer?.userTimezone);
  return resolveDisplayTz(userTz);
}

/**
 * The zone the viewer has actually stored, or null when there is none yet.
 *
 * For the few places that must tell "has not chosen" apart from "chose UTC".
 * useDisplayTz floors an unset user to UTC so every timestamp still renders,
 * which is right for rendering and wrong for a label: the profile menu row read
 * "UTC" for a brand-new user while the picker behind it preselected their device
 * zone, which looked like a setting already made, so pressing Cancel left them on
 * UTC. Render times with useDisplayTz; label the setting with this.
 */
export function useStoredDisplayTz(): string | null {
  return useSelector((s: IRootReducerState) => s.userPreferencesReducer?.userTimezone ?? null);
}

/**
 * Loads the viewer's display zone into the store once per session, and seeds it
 * from their device the first time they ever sign in.
 *
 * Mounted once, app-wide, by DisplayTzLoader. Runs when the access token lands,
 * because the preferences row is keyed by user and nothing about it is per
 * instance: switching tenant or instance does a full page reload anyway, which
 * remounts this and re-reads it.
 *
 * The seeding write is what keeps the client and the server agreeing. Without a
 * stored zone the client would have to fall back to the device to show anything
 * useful, while the server has no device to fall back to, and the two would
 * quietly format the same timestamp differently. Writing the device zone once
 * gives both ends the same answer to read.
 */
export function useLoadDisplayTzPrefs(): void {
  const dispatch = useDispatch();
  const accessToken = useSelector((s: IRootReducerState) => s.authReducer?.accessToken);
  // Once per mount after it has worked, not once per token refresh: MSAL renews
  // the token silently and re-fetching on every renewal would be pointless.
  //
  // Marked loaded on success only. It used to be set before the request went
  // out, so one network blip or a 5xx during boot left the user on the UTC floor
  // for the rest of the session with nothing on screen to say why. Leaving it
  // unset lets the next token renewal try again. An overlapping retry is
  // harmless: the GET is a read, and the seed is set-once in SQL.
  const loaded = useRef(false);

  useEffect(() => {
    if (!accessToken || loaded.current) return;

    let cancelled = false;

    (async () => {
      try {
        const headers = await getAuthorizationHeader();
        const res = await fetch("/api/v1/user-preferences", {
          method: "GET",
          headers,
          cache: "no-store",
        });
        if (!res.ok || cancelled) return;
        loaded.current = true;

        const data = (await res.json()) as { timezone?: string | null };
        let zone = data?.timezone ?? null;

        if (!zone) {
          // Set-once seeding. The endpoint decides whether the write happens
          // (`WHERE timezone IS NULL` in SQL), so two tabs racing on first load
          // cannot fight, and a user who has already chosen a zone is never
          // overwritten by a device they happen to be signing in from.
          const deviceZone = systemDisplayTimezone();
          if (deviceZone) {
            try {
              const seedRes = await fetch("/api/v1/user-preferences/default-timezone", {
                method: "PUT",
                headers: { ...headers, "Content-Type": "application/json" },
                body: JSON.stringify({ timezone: deviceZone }),
              });
              if (seedRes.ok) {
                const seeded = (await seedRes.json()) as { timezone?: string | null };
                zone = seeded?.timezone ?? null;
              }
            } catch {
              // Non-fatal. An unset zone floors to UTC and the picker still
              // works; do not let a preference write break app boot.
              logger.warn("[useDisplayTz] could not seed the default timezone");
            }
          }
        }

        if (!cancelled) dispatch(setUserTimezone(zone));
      } catch {
        logger.warn("[useDisplayTz] could not load the display timezone");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, dispatch]);
}
